/** The operations summary of every client account, shared by every screen that shows it. */
import { useQuery, type UseQueryResult } from "@tanstack/react-query";

import { readPlatformClientOperations, type ClientOperationsResponse } from "@/shared/api";
import { validatePeriod, type SelectedPeriod } from "@/shared/period/period";

export const OPERATIONS_KEY = ["platform-client-operations"] as const;

/** The analytics' own default, so a screen without a control agrees with one that has it. */
export const DEFAULT_PERIOD: SelectedPeriod = { since: null, until: null, preset: "30d" };

export function useClientOperations(period: SelectedPeriod = DEFAULT_PERIOD): UseQueryResult<ClientOperationsResponse[]> {
  const validation = validatePeriod(period);
  return useQuery({
    queryKey: [...OPERATIONS_KEY, period.since, period.until, period.preset],
    enabled: validation.kind !== "invalid",
    retry: false,
    refetchInterval: 30_000,
    queryFn: async () => {
      const result = await readPlatformClientOperations({
        cache: "no-store",
        query:
          validation.kind === "exact"
            ? { since: validation.since, until: validation.until }
            : { preset: period.preset === "custom" ? null : period.preset },
      });
      if (result.data === undefined) throw new Error("Client operations unavailable");
      return result.data;
    },
  });
}
